/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Table } from '@/components/Table/Table';
import { TableBody } from '@/components/TableBody/TableBody';
import { TableCell } from '@/components/TableCell/TableCell';
import { TableRow } from '@/components/TableRow/TableRow';
import { TableCaption } from '@/components/TableCaption/TableCaption';
import { useYearsTable } from '../../api/getYearsTable';
import azCommonStyles from '@/theme/globals.module.scss';
import ArrowDown from '@/public/images/arrow_down.png';
import styles from './YearsTable.module.scss';
import cx from 'classnames';
import Title from '@/components/AZCustomComponent/Title';
import { useRef, useState } from 'react';
import NextImage from '@/components/NextImage';
import { Text } from '@az/starc-ui';
import { useLabels } from '@/hooks/useLabels';
import { useSelector } from 'react-redux';
import type { ReduxState } from '@/types';

const labelsMap = {
  see_More_Button: 'label_YearsTable_See_More_Button',
  see_Less_Button: 'label_YearsTable_See_Less_Button',
  variation_Note: 'label_YearsTable_Variation_Note',
};

export type YearsTableProps = {
  productId: string | null | undefined;
  make: string | undefined;
  model: string | undefined;
  hasAssociatedPartType: boolean | undefined;
};

export function YearsTable({ productId, make, model, hasAssociatedPartType }: YearsTableProps) {
  const deviceType = useSelector(({ appData }: ReduxState) => appData.deviceType);
  const [expanded, setExpanded] = useState<boolean>(() => deviceType === 'bot');
  const tableRef = useRef<HTMLDivElement>(null);
  const labels = useLabels(labelsMap);

  const { data } = useYearsTable({
    hasAssociatedPartType,
    make: make || undefined,
    model: model || undefined,
    productId,
  });

  return data?.values.length && data.attributes.length && data.tableName ? (
    <div className={styles.yearsTableContainer}>
      <div ref={tableRef} className={styles.tableWrapper}>
        <Table className={styles.yearsTable}>
          <TableCaption captionside="top">
            <Title
              firstText={data.tableName}
              firstTextClass={styles.yearsTableTitle}
              underline
              overRuleH1="h3"
            />
          </TableCaption>
          <thead className={styles.tableHeaderRow}>
            <TableRow>
              {data.attributes.map((attribute) => (
                <TableCell component="th" key={attribute}>
                  {attribute}
                </TableCell>
              ))}
            </TableRow>
          </thead>
          <TableBody>
            {data.values.slice(0, expanded ? data.values.length : 5).map((row, yearIndex) => {
              const numOfCols = data.attributes.length;
              return (
                <TableRow
                  key={`${row.year}`}
                  className={cx({ [styles.grayRow]: yearIndex % 2 === 1 }, styles.tableDataRow)}
                >
                  <TableCell key={row.year} align="center">
                    {row.year}
                  </TableCell>

                  {Array.from({ length: numOfCols - 1 }).map((_, colIndex) => {
                    const values = row.attributeValues[colIndex];
                    return (
                      <TableCell key={`${row.year}_${colIndex}`} align="center">
                        {values === null ? (
                          '-'
                        ) : (
                          <div>
                            {values.map((cellValue, i) => (
                              <div key={`${row.year}_${colIndex}_${i}`} aria-hidden>
                                {cellValue}
                              </div>
                            ))}
                            <div className="sr-only">{values.join(', ')}</div>
                          </div>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className={styles.variationNoteContainer}>
        <Text className={styles.variationNote}>{labels.variation_Note}</Text>
      </div>
      <button
        className={cx(azCommonStyles['az-button-text'], styles.seeMoreYearsButton)}
        onClick={() => {
          setExpanded((value) => !value);
          tableRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'end' });
        }}
        data-testid={expanded ? 'see-less-years-button' : 'see-all-years-button'}
      >
        {expanded ? labels.see_Less_Button : labels.see_More_Button}
        <NextImage
          src={ArrowDown.src}
          className={cx([styles.arrow, { [styles.arrowReversed]: expanded }])}
          width={20}
          height={12}
        />
      </button>
    </div>
  ) : null;
}
